import React, { FC, useContext } from 'react';
import COButton from 'shared/components/COButton';
import { InfoCircleOutlined } from '@ant-design/icons';
import { TargetTexts } from './constants';
import styles from './target.module.scss';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { convertToReadableText } from 'shared/utils/convertToUserReadable';
import { ReactComponent as TargetIcon } from 'assets/icons/target.svg';
import CreateTargetModal from './CreateTargetModal';
import { SbtiTarget } from 'models/Sbti/sbti.model';
import { ParentOrganization } from 'models/Target/Target.model';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { useUserRole } from 'shared/hooks/useUserRole';
import { canCreate } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

interface HeaderProps {
  selectedTargetId?: number;
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isCustomTarget?: boolean;
  setIsCustomTarget: React.Dispatch<React.SetStateAction<boolean>>;
  sbtiTargets?: SbtiTarget[];
  fetchSbtiTargets?: () => Promise<void>;
  isEdit?: boolean;
  setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  getParentOrganization?: (type?: {
    isCustom?: boolean | undefined;
    isEmptySate?: boolean;
  }) => Promise<ParentOrganization | undefined>;
  organization?: ParentOrganization;
  parentOrgLoading?: boolean;
  isCustomButtonLoading?: boolean;
  setSelectedTarget?: React.Dispatch<React.SetStateAction<string>>;
  setSelectedTargetId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number>>;
}
const Header: FC<HeaderProps> = props => {
  const {
    selectedTargetId,
    visible,
    setVisible,
    isCustomTarget,
    setIsCustomTarget,
    sbtiTargets,
    fetchSbtiTargets,
    isEdit,
    setIsEdit,
    getParentOrganization,
    organization,
    parentOrgLoading,
    isCustomButtonLoading,
    setSelectedTarget,
    setSelectedTargetId,
    setSelectedIndex
  } = props;
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { organizationData } = useParentOrganizationContext();
  const userRole = useUserRole();
  const canCreateTarget = canCreate(userRole);
  
  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);
    
  const handleModalOpen = async () => {
    if (!canCreateTarget) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    const response = await getParentOrganization?.();
    if (response?.name) {
      setVisible(true);
      setIsCustomTarget(false);
      setIsEdit?.(false);
    }
  };
  
  const customTargetHandler = async () => {
    if (!canCreateTarget) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    const response = await getParentOrganization?.({ isCustom: true });
    if (response?.name) {
      setVisible(true);
      setIsCustomTarget(true);
      setIsEdit?.(false);
    }
  };
  return (
    <div className={styles.header}>
      <div className={styles.heading}>
        <TargetIcon className={styles.targetIcon} />
        {TargetTexts.TARGETS_AND_INITIATIVES}
        <InfoCircleOutlined
          onClick={handleToolTipOpen(
            convertToReadableText(HelpDeskInputFieldEnum.targetInitiative) as HelpDeskInputFieldEnum
          )}
          className={styles.infoIcon}
        />
      </div>
      {organizationData?.organization && <div className={styles.btnRow}>
        <COButton
          onClick={handleModalOpen}
          loading={parentOrgLoading}
          disabled={!canCreateTarget}>
          <Add /> {TargetTexts.ADD_SCIENCE_BASED_TARGET}
        </COButton>
        <COButton
          onClick={customTargetHandler}
          loading={isCustomButtonLoading}
          disabled={!canCreateTarget}>
          <Add /> {TargetTexts.ADD_CUSTOM_TARGET}
        </COButton>
      </div>}
      <CreateTargetModal
        isCustomTarget={isCustomTarget}
        visible={visible}
        setVisible={setVisible}
        selectedTargetId={selectedTargetId}
        sbtiTargets={sbtiTargets}
        fetchSbtiTargets={fetchSbtiTargets}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        organization={organization}
        setSelectedTarget={setSelectedTarget}
        setSelectedTargetId={setSelectedTargetId}
        setSelectedIndex={setSelectedIndex}
      />
    </div>
  );
};

export default Header;
